import React, { useEffect, useState } from 'react'
import { parentList } from '../utils/parentNode'
import { Link } from 'gatsby'
import { Search } from './search'

interface Props {
  data: any
  isOpen: boolean
  setIsOpen: (value: boolean) => void
  activeMenu: string
  setActiveMenu: React.Dispatch<React.SetStateAction<string>>
}
type Menu = {
  path: string
  cssClasses?: string
  label?: string
  target?: string
}

const Navigation: React.FC<Props> = ({ data, isOpen, setIsOpen, activeMenu, setActiveMenu }) => {
  const primaryMenu = data?.primaryMenu?.menuItems?.nodes
  const secondaryMenu = data?.secondaryMenu?.menuItems?.nodes
  const [isSearching, setIsSearching] = useState<boolean>(false)

  useEffect(() => {
    const html = document.querySelector('html')
    const breakpoint = window.innerWidth < 1099

    if (!html) return

    if (isOpen && breakpoint) {
      html.style.overflow = 'hidden'
    } else {
      html.style.overflow = 'auto'
    }
  }, [isOpen])

  const handleOpen = (): void => {
    setActiveMenu('')
    setIsOpen(true)
  }

  const toggleSearch = (state: boolean): void => {
    setIsSearching(!state)
  }

  function openMenuDropdown(target = ''): void {
    if (target === '/client-success/') return
    setActiveMenu(target)
    setIsOpen(true)
  }

  function handleClose(): void {
    setIsOpen(false)
  }

  const activeMenuClass = (menu: Menu): string => {
    if (isOpen && menu.path === activeMenu) {
      return 'text-blue'
    }

    return 'text-gray'
  }

  const ActiveSearch = (): JSX.Element => {
    return (
      <div className='pl-2 flex justify-end w-full'>
        <Search autoFocus={true} defaultActiveItemId={0} placeholder='Search' />

        <button
          type='button'
          aria-label='Search'
          className='p-2 text-gray hover:text-blue-sky'
          onClick={() => {
            toggleSearch(isSearching)
          }}
        >
          <svg width='20' height='20' viewBox='0 0 20 20' fill='currentColor'>
            <path
              d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z'
              fillRule='evenodd'
              clipRule='evenodd'
            />
          </svg>
        </button>
      </div>
    )
  }

  return (
    <>
      <nav className='navigation w-full'>
        <div className='navigation--mobile flex justify-end lg:hidden'>
          {!isSearching && (
            <button
              type='button'
              aria-label='Search'
              className='p-2'
              onClick={() => {
                toggleSearch(isSearching)
              }}
            >
              <svg
                className='cursor-pointer text-gray hover:text-blue-sky'
                xmlns='http://www.w3.org/2000/svg'
                width='14.997'
                height='15'
                viewBox='0 0 14.997 15'
              >
                <path
                  d='M14.793,12.968l-2.921-2.921a.7.7,0,0,0-.5-.205H10.9A6.09,6.09,0,1,0,9.843,10.9v.477a.7.7,0,0,0,.205.5l2.921,2.921a.7.7,0,0,0,.993,0l.829-.829A.706.706,0,0,0,14.793,12.968Zm-8.7-3.126a3.75,3.75,0,1,1,3.75-3.75A3.747,3.747,0,0,1,6.093,9.843Z'
                  fill='currentColor'
                />
              </svg>
            </button>
          )}
          {isSearching && <ActiveSearch />}
          {!isSearching && (
            <button
              type='button'
              aria-label='Mobile Menu'
              className='p-2'
              onClick={() => {
                handleOpen()
              }}
            >
              <svg
                className='hamburger-menu text-blue'
                xmlns='http://www.w3.org/2000/svg'
                width='21'
                height='17'
                viewBox='0 0 21 17'
              >
                <g transform='translate(1.5 1.5)'>
                  <path
                    d='M4.5,18h18'
                    transform='translate(-4.5 -11.25)'
                    fill='none'
                    stroke='currentColor'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='3'
                  />
                  <path
                    d='M4.5,9h18'
                    transform='translate(-4.5 -9)'
                    fill='none'
                    stroke='currentColor'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='3'
                  />
                  <path
                    d='M4.5,27h18'
                    transform='translate(-4.5 -13.5)'
                    fill='none'
                    stroke='currentColor'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='3'
                  />
                </g>
              </svg>
            </button>
          )}
        </div>
        <div className='navigation--desktop hidden lg:flex justify-between'>
          <div className={`nav-primary flex items-center ${isSearching ? 'hidden' : ''}`}>
            {parentList(primaryMenu).map((menuItem) => {
              const classNames = menuItem?.cssClasses?.join(' ')
              const activeMenuLinkClass = activeMenuClass(menuItem)
              {
                if (menuItem.path == '/client-success/') {
                  return (
                    <Link
                      className={`btn-link text-gray py-6 px-3 ${classNames} `}
                      key={menuItem.path}
                      to={menuItem.path}
                    >
                      {menuItem.label}
                    </Link>
                  )
                } else {
                  return (
                    <Link
                      className={`
                        btn-link
                        py-6
                        px-3
                        ${classNames}
                        ${activeMenuLinkClass}
                      `}
                      key={menuItem.path}
                      to={menuItem.path}
                      onMouseEnter={() => {
                        openMenuDropdown(menuItem.url)
                      }}
                      onMouseLeave={() => {
                        handleClose()
                      }}
                    >
                      {menuItem.label}
                    </Link>
                  )
                }
              }
            })}
          </div>
          {isSearching && <ActiveSearch />}
          <div className='nav-secondary flex items-center justify-end'>
            {!isSearching && (
              <button
                type='button'
                aria-label='Search'
                className='p-2'
                onClick={() => {
                  toggleSearch(isSearching)
                }}
              >
                <svg
                  className='cursor-pointer text-gray hover:text-blue'
                  xmlns='http://www.w3.org/2000/svg'
                  width='14.997'
                  height='15'
                  viewBox='0 0 14.997 15'
                >
                  <path
                    d='M14.793,12.968l-2.921-2.921a.7.7,0,0,0-.5-.205H10.9A6.09,6.09,0,1,0,9.843,10.9v.477a.7.7,0,0,0,.205.5l2.921,2.921a.7.7,0,0,0,.993,0l.829-.829A.706.706,0,0,0,14.793,12.968Zm-8.7-3.126a3.75,3.75,0,1,1,3.75-3.75A3.747,3.747,0,0,1,6.093,9.843Z'
                    fill='currentColor'
                  />
                </svg>
              </button>
            )}
            {secondaryMenu.map((menuItem: Menu) => {
              const classNames = menuItem?.cssClasses?.join(' ')
              return (
                <a
                  className={
                    classNames ? `btn-link text-gray px-3 ${classNames}` : 'btn-link text-gray px-3'
                  }
                  key={menuItem.path}
                  href={menuItem.path}
                  target={menuItem.target}
                  rel='noopener noreferrer'
                >
                  {menuItem.label}
                </a>
              )
            })}
          </div>
        </div>
      </nav>
    </>
  )
}

export default Navigation
